import React from 'react';
import isEmpty from 'lodash.isempty';
import sortBy from 'lodash.sortby';
import moment from 'moment';
import axiosInstance from '../actions/AxiosInstance';
import api from '../actions/EndPoints';
import { groupStatus } from './shared/Content';

/**
 * Hook providing appointment-related API services
 * @param {Function} dispatch - Redux dispatch function for state updates
 * @returns {Object} Object containing appointment service functions
 */
export const useAppointmentServices = (dispatch) => {
    /**
     * Fetches and processes appointment details, filtering for future scheduled appointments
     * @param {string} key - API key for authentication
     */
    const getAppointmentDetails = React.useCallback(async (key, externalGroupId) => {
        try {
            const getApptData = await axiosInstance.get(api.appointmentDetail(externalGroupId), {
                headers: { apikey: `${key}` }
            });
            if (getApptData) {
                const { data } = getApptData;
                const rescheduleEligibleAppts = data?.results?.filter(appts =>
                    appts.invitation &&
                    appts.invitation.reschedulingLink !== '' &&
                    appts.invitation.status === 'Scheduled');

                const futureInvitations = rescheduleEligibleAppts?.filter(appts =>
                    (moment(appts.endTime) >= moment()));

                const sortedByCreatedDates = futureInvitations && sortBy(futureInvitations, 'created').reverse();

                const apptsToDisplay = sortedByCreatedDates && sortedByCreatedDates.reduce(
                    (accumulator, appointment) => Object.assign(accumulator, {[appointment.invitation.appointmentType]: appointment}), {});

                if (apptsToDisplay) {
                    dispatch({
                        type: 'APPOINTMENT_DATA',
                        invitations: apptsToDisplay,
                        apptDataLoading: false,
                    });
                } else {
                    dispatch({
                        type: 'APPOINTMENT_DATA',
                        apptDataLoading: false,
                        invitations: {}
                    });
                }
            }
        } catch (err) {
            dispatch({
                type: 'TIME_TRADE_FAILURE',
                apptDataLoading: false,
                timeTradeFailure: true
            });
            console.log(err)
            window.metricsData.error = 'Timetrade Failure Error';
        }
    }, [dispatch]);

    /**
     * Redirects to TimeTrade calendar API and processes the response
     * @param {string} key - API key for authentication
     * @param {string} checkStatus - Current engagement status
     * @param {Array} pastEngagements - Array of past engagements
     */
    const redirectToTimeTrade = React.useCallback(async (key, checkStatus, pastEngagements) => {
        const schedulingLinkStatus = [groupStatus.APPOINTMENT_NEEDED, groupStatus.FINANCIAL_ACCOUNTS, groupStatus];
        const calendarTTUrl = !schedulingLinkStatus.includes(checkStatus) && !isEmpty(pastEngagements) ?
            `${api.calendarTTAPI}?excludeSchedulingLink=true` : api.calendarTTAPI;
        try {
            const getTTData = await axiosInstance.get(calendarTTUrl, {
                headers: { apikey: `${key}` }
            });
            if (getTTData) {
                const { data } = getTTData;
                if (data !== undefined) {
                    dispatch({
                        type: 'TIME_TRADE_DATA',
                        territoryName: data.territoryName,
                        tType: data.type,
                        parentTerritory: data.parentTerritory,
                        employeeName: data.employeeName,
                        employeeRole: data.employeeRole,
                        invitationId: data.invitationId,
                        employeePhone: data.employeePhone,
                        employeeEmail: data.employeeEmail,
                        schedulingLink: data.schedulingLink,
                        adviceQuestionnaireLink: data.adviceQuestionnaireLink,
                    });
                }
            }
        } catch (err) {
            console.log(err);
            dispatch({
                type: 'TIME_TRADE_FAILURE',
                apptDataLoading: true,
                timeTradeFailure: true
            });
        }
    }, [dispatch]);

    return { getAppointmentDetails, redirectToTimeTrade };
};
